import { Check } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * SelectionCard — large, opinionated radio / checkbox card.
 *
 * Used for "pick a plan", "pick a coverage tier", and product selection
 * flows where each option needs more presentation than a stock radio
 * affords (icon, title, description, price, badges).
 *
 * Two selection modes:
 *   - 'radio' (default) — exactly one card in a SelectionCardGroup can be
 *     selected at a time.
 *   - 'checkbox' — multiple cards can be selected independently.
 *
 * Dumb / controlled: parent owns the selection state. Card click toggles
 * `onChange` with the new boolean.
 */

export type SelectionCardMode = "radio" | "checkbox";

export interface SelectionCardProps
  extends Omit<React.HTMLAttributes<HTMLLabelElement>, "onChange" | "children" | "title"> {
  /** Whether this card is selected. */
  selected: boolean;
  onChange: (next: boolean) => void;
  /** Selection mode — affects the indicator shape (radio dot vs checkbox). */
  mode?: SelectionCardMode;
  /** Required for form submission / accessibility — same `name` for radios in a group. */
  name?: string;
  /** Value submitted for this card. */
  value?: string;
  /** Card title — usually a noun phrase. */
  title: React.ReactNode;
  /** Optional eyebrow above title (e.g. "Most popular"). */
  eyebrow?: React.ReactNode;
  /** Supporting copy. */
  description?: React.ReactNode;
  /** Leading icon / illustration. */
  icon?: React.ReactNode;
  /** Trailing badge / price slot. */
  trailing?: React.ReactNode;
  /** Optional nested content (additional rows shown when selected). */
  children?: React.ReactNode;
  /** Disable interaction. */
  disabled?: boolean;
}

export const SelectionCard = React.forwardRef<HTMLLabelElement, SelectionCardProps>(
  function SelectionCard(
    {
      children,
      className,
      description,
      disabled,
      eyebrow,
      icon,
      mode = "radio",
      name,
      onChange,
      selected,
      title,
      trailing,
      value,
      ...props
    },
    ref,
  ) {
    return (
      <label
        ref={ref}
        className={cx(
          "cw-v2-selection-card",
          selected && "cw-v2-selection-card--selected",
          disabled && "cw-v2-selection-card--disabled",
          className,
        )}
        {...props}
      >
        <input
          type={mode === "radio" ? "radio" : "checkbox"}
          name={name}
          value={value}
          checked={selected}
          disabled={disabled}
          onChange={(event) => {
            if (mode === "radio") {
              onChange(true);
            } else {
              onChange(event.target.checked);
            }
          }}
          className="cw-v2-selection-card__input"
        />
        <span className="cw-v2-selection-card__header">
          {icon ? (
            <span className="cw-v2-selection-card__icon" aria-hidden>
              {icon}
            </span>
          ) : null}
          <span
            aria-hidden
            className={cx(
              "cw-v2-selection-card__indicator",
              `cw-v2-selection-card__indicator--${mode}`,
            )}
          >
            {mode === "checkbox" && selected ? (
              <Check className="cw-v2-selection-card__indicator-check" aria-hidden />
            ) : null}
          </span>
        </span>
        <span className="cw-v2-selection-card__body">
          {eyebrow ? <span className="cw-v2-selection-card__eyebrow">{eyebrow}</span> : null}
          <span className="cw-v2-selection-card__title-row">
            <span className="cw-v2-selection-card__title">{title}</span>
            {trailing ? <span className="cw-v2-selection-card__trailing">{trailing}</span> : null}
          </span>
          {description ? (
            <span className="cw-v2-selection-card__description">{description}</span>
          ) : null}
          {children ? <span className="cw-v2-selection-card__extra">{children}</span> : null}
        </span>
      </label>
    );
  },
);
SelectionCard.displayName = "SelectionCard";

export interface SelectionCardGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns at md+. Default 1 (single column). */
  columns?: 1 | 2 | 3;
  ariaLabel?: string;
}

/** Optional grid wrapper that lays out selection cards in n columns. */
export const SelectionCardGroup = React.forwardRef<HTMLDivElement, SelectionCardGroupProps>(
  function SelectionCardGroup({ ariaLabel, className, columns = 1, ...props }, ref) {
    return (
      <div
        ref={ref}
        role="group"
        aria-label={ariaLabel}
        className={cx(
          "cw-v2-selection-card-group",
          `cw-v2-selection-card-group--cols-${columns}`,
          className,
        )}
        {...props}
      />
    );
  },
);
SelectionCardGroup.displayName = "SelectionCardGroup";
